import type { DeleteTestDlnsRequest, DeleteTestDlnsResponse } from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

/**
 * Delete test DLNs for a company
 *
 * This endpoint deletes all test DLNs of a buyer company. Test DLNs are identified
 * based on their DLN number (starts with "test-").
 *
 * If dry_run is true, this will return the number of DLN mains and chains that are affected,
 * but doesn't delete them.
 *
 * @param request - The delete test DLNs request payload
 * @returns Promise resolving to the count of affected DLNs
 *
 * @see https://api-docs.vestigas.com/endpoints/delete-test-dlns
 */
export const deleteTestDlns = async (
  request: DeleteTestDlnsRequest,
): Promise<DeleteTestDlnsResponse> => {
  try {
    const response = await vestigasApi
      .post(ENDPOINT.TEST_DLNS.DELETE(), { json: request })
      .json<DeleteTestDlnsResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error deleting test DLNs',
      error,
    });

    if (error instanceof HTTPError) {
      let errorMessage = '';

      switch (error.response.status) {
        case 400: {
          errorMessage = i18n.t(($) => $.delivery.deleteTestDlns.error400, {
            ns: 'data',
          });
          break;
        }

        case 404: {
          errorMessage = i18n.t(($) => $.delivery.deleteTestDlns.error404, {
            ns: 'data',
            buyerId: request.buyerId,
          });
          break;
        }

        case 422: {
          errorMessage = i18n.t(($) => $.delivery.deleteTestDlns.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.delivery.deleteTestDlns.error.generic,
            {
              ns: 'data',
            },
          );
        }
      }

      ToastService.error(errorMessage);
    }

    throw error;
  }
};
